export type FilingTypeCategory =
    | 'Periodic Reports'
    | 'Registration Statements'
    | 'Proxy & Ownership'
    | 'Investment Managers'
    | 'Other Common Types';

export interface FilingTypeMetadata {
    apiValue: string;
    label: string;
    description: string;
    category: FilingTypeCategory;
}

export const FILING_TYPE_CATEGORIES: readonly FilingTypeCategory[] = [
    'Periodic Reports',
    'Registration Statements',
    'Proxy & Ownership',
    'Investment Managers',
    'Other Common Types',
] as const;

export const FILING_TYPE_ALLOWLIST: readonly FilingTypeMetadata[] = [
    // Periodic Reports
    {
        apiValue: '10-K',
        label: '10-K',
        description: 'Annual report',
        category: 'Periodic Reports',
    },
    {
        apiValue: '10-Q',
        label: '10-Q',
        description: 'Quarterly report',
        category: 'Periodic Reports',
    },
    {
        apiValue: '8-K',
        label: '8-K',
        description: 'Current report (material events)',
        category: 'Periodic Reports',
    },
    {
        apiValue: '20-F',
        label: '20-F',
        description: 'Annual report (foreign private issuer)',
        category: 'Periodic Reports',
    },
    {
        apiValue: '6-K',
        label: '6-K',
        description: 'Semi-annual/interim report (foreign private issuer)',
        category: 'Periodic Reports',
    },

    // Registration Statements
    {
        apiValue: 'S-1',
        label: 'S-1',
        description: 'IPO registration statement',
        category: 'Registration Statements',
    },
    {
        apiValue: 'S-3',
        label: 'S-3',
        description: 'Shelf registration statement',
        category: 'Registration Statements',
    },
    {
        apiValue: 'S-4',
        label: 'S-4',
        description: 'Business combination registration',
        category: 'Registration Statements',
    },
    {
        apiValue: 'F-1',
        label: 'F-1',
        description: 'IPO registration (foreign private issuer)',
        category: 'Registration Statements',
    },

    // Proxy & Ownership
    {
        apiValue: 'DEF 14A',
        label: 'DEF 14A',
        description: 'Definitive proxy statement',
        category: 'Proxy & Ownership',
    },
    {
        apiValue: '3',
        label: 'Form 3',
        description: 'Initial statement of beneficial ownership',
        category: 'Proxy & Ownership',
    },
    {
        apiValue: '4',
        label: 'Form 4',
        description: 'Change in beneficial ownership',
        category: 'Proxy & Ownership',
    },
    {
        apiValue: '5',
        label: 'Form 5',
        description: 'Annual statement of beneficial ownership',
        category: 'Proxy & Ownership',
    },
    {
        apiValue: 'SC 13G',
        label: 'SC 13G',
        description: 'Beneficial ownership report (passive investor)',
        category: 'Proxy & Ownership',
    },
    {
        apiValue: 'SC 13D',
        label: 'SC 13D',
        description: 'Beneficial ownership report (active investor)',
        category: 'Proxy & Ownership',
    },

    // Investment Managers
    {
        apiValue: '13F-HR',
        label: '13F',
        description: 'Institutional investment manager holdings',
        category: 'Investment Managers',
    },
    {
        apiValue: '13D',
        label: '13D',
        description: 'Beneficial ownership (activist)',
        category: 'Investment Managers',
    },
    {
        apiValue: '13G',
        label: '13G',
        description: 'Beneficial ownership (passive)',
        category: 'Investment Managers',
    },

    // Other Common Types
    {
        apiValue: 'D',
        label: 'Form D',
        description: 'Regulation D offering notice',
        category: 'Other Common Types',
    },
    {
        apiValue: 'NT 10-K',
        label: 'NT 10-K',
        description: 'Late annual report notification',
        category: 'Other Common Types',
    },
    {
        apiValue: 'NT 10-Q',
        label: 'NT 10-Q',
        description: 'Late quarterly report notification',
        category: 'Other Common Types',
    },
    {
        apiValue: 'DEFA14A',
        label: 'DEFA14A',
        description: 'Additional definitive proxy materials',
        category: 'Other Common Types',
    },
    {
        apiValue: '25',
        label: '25',
        description: 'Exchange delisting notification',
        category: 'Other Common Types',
    },
] as const;

export const ALLOWED_FILING_TYPE_VALUES: ReadonlySet<string> = new Set(
    FILING_TYPE_ALLOWLIST.map((entry) => entry.apiValue),
);

export const FILING_TYPE_METADATA_BY_API_VALUE: ReadonlyMap<string, FilingTypeMetadata> = new Map(
    FILING_TYPE_ALLOWLIST.map((entry) => [entry.apiValue, entry]),
);

export const FILING_TYPES_BY_CATEGORY: ReadonlyMap<FilingTypeCategory, FilingTypeMetadata[]> =
    FILING_TYPE_ALLOWLIST.reduce((map, entry) => {
        const existing = map.get(entry.category);
        if (existing) {
            existing.push(entry);
        } else {
            map.set(entry.category, [entry]);
        }
        return map;
    }, new Map<FilingTypeCategory, FilingTypeMetadata[]>());

export function filterToAllowedTypes(types: string[]): string[] {
    return types.filter((type) => ALLOWED_FILING_TYPE_VALUES.has(type));
}
